import React from 'react'
import { Box, Button, Divider, IconButton, Switch, Tooltip, Typography } from '@mui/material'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded'
import { DndContext, closestCenter } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { LayoutControls } from '../api/useDashboardLayout'
import { ProviderRoleId, useCurrentRole } from '../currentRole'
import { advancedColors, onDark } from '../theme'
import DragHandle from './DragHandle'
import { LayoutDefinition } from './layout'
import {
  CATEGORY_ORDER,
  PANEL_CATEGORY_LABELS,
  PANELS,
  PanelCategory,
  PanelDefinition,
  PanelOfferScope,
  isPanelOfferable,
  panelsInCategory,
} from './registry'
import SortableRow from './SortableRow'
import { useReorderDnd } from './useReorderDnd'

type Props = {
  layout: LayoutDefinition
  isCustom: boolean
  saveFailed: boolean
  controls: LayoutControls
  onClose: () => void
  scope: PanelOfferScope
}

const PanelToggleRow = ({
  panel,
  checked,
  onToggle,
}: {
  panel: PanelDefinition
  checked: boolean
  onToggle: () => void
}) => (
  <SortableRow
    id={panel.id}
    disabled={!checked}
    sortable={checked}
    sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}
  >
    {({ attributes, listeners }) => (
      <>
        {checked ? (
          <DragHandle attributes={attributes} listeners={listeners} label={`Drag to reorder — ${panel.title}`} />
        ) : (
          <Box sx={{ width: 30 }} />
        )}
        <Typography variant="body2" sx={{ flex: 1, color: onDark.primary }}>
          {panel.title}
        </Typography>
        <Switch
          size="small"
          checked={checked}
          onChange={onToggle}
          inputProps={{ 'aria-label': `Show ${panel.title}` }}
        />
      </>
    )}
  </SortableRow>
)

type SectionProps = {
  category: PanelCategory
  inCategory: PanelDefinition[]
  layout: LayoutDefinition
  controls: LayoutControls
}

const offerableInCategory = (
  category: PanelCategory,
  role: ProviderRoleId,
  scope: PanelOfferScope
): PanelDefinition[] => panelsInCategory(category).filter((panel) => isPanelOfferable(panel.id, role, scope))

const CustomizeBarSection = ({ category, inCategory, layout, controls }: SectionProps) => {
  const placedIds = new Set(layout.panels.map((entry) => entry.panel))
  const placed = layout.panels.filter((entry) => PANELS[entry.panel].category === category)
  // Already-placed panels stay toggleable even when no longer offerable.
  const rows = [
    ...placed.map((entry) => PANELS[entry.panel]),
    ...inCategory.filter((panel) => !placedIds.has(panel.id)),
  ]
  const allOn = inCategory.length > 0 && inCategory.every((panel) => placedIds.has(panel.id))

  const { sensors, onDragEnd } = useReorderDnd(
    layout.panels.map((entry) => entry.panel),
    controls.moveTo
  )

  if (rows.length === 0) {
    return null
  }

  return (
    <Box sx={{ py: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="overline" sx={{ color: onDark.secondary }}>
          {PANEL_CATEGORY_LABELS[category]}
        </Typography>
        <Tooltip
          title={
            allOn ? `Hide all of ${PANEL_CATEGORY_LABELS[category]}` : `Show all of ${PANEL_CATEGORY_LABELS[category]}`
          }
        >
          <Switch
            size="small"
            disabled={inCategory.length === 0}
            checked={allOn}
            onChange={() =>
              allOn ? controls.removeMany(inCategory.map((p) => p.id)) : controls.addMany(inCategory.map((p) => p.id))
            }
            inputProps={{ 'aria-label': `Show all of ${PANEL_CATEGORY_LABELS[category]}` }}
          />
        </Tooltip>
      </Box>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={placed.map((entry) => entry.panel)} strategy={verticalListSortingStrategy}>
          {rows.map((panel) => (
            <PanelToggleRow
              key={panel.id}
              panel={panel}
              checked={placedIds.has(panel.id)}
              onToggle={() =>
                placedIds.has(panel.id)
                  ? controls.remove(layout.panels.findIndex((entry) => entry.panel === panel.id))
                  : controls.add(panel.id)
              }
            />
          ))}
        </SortableContext>
      </DndContext>
    </Box>
  )
}

const DashboardCustomizeBar = ({ layout, isCustom, saveFailed, controls, onClose, scope }: Props) => {
  const [role] = useCurrentRole()

  return (
    <Box
      sx={{
        flexShrink: 0,
        height: 300,
        maxHeight: '50%',
        overflowY: 'auto',
        backgroundColor: advancedColors.deep,
        borderTop: `1px solid ${onDark.stroke}`,
        px: 3,
        py: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Typography variant="h5" sx={{ color: onDark.primary }}>
          Customize dashboard
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isCustom && (
            <Button
              size="small"
              startIcon={<RestartAltRoundedIcon />}
              onClick={controls.reset}
              sx={{ color: onDark.primary }}
            >
              Reset layout
            </Button>
          )}
          <Tooltip title="Close">
            <IconButton
              size="small"
              aria-label="Close customize dashboard"
              onClick={onClose}
              sx={{ color: onDark.primary }}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {saveFailed && (
        <Typography variant="subtitle2" sx={{ mt: 1, color: onDark.accentRed, fontWeight: 600 }}>
          Your layout could not be saved. It is applied here but other devices will not see it.
        </Typography>
      )}

      <Divider sx={{ mt: 1.5, borderColor: onDark.stroke }} />

      <Box
        sx={{
          mt: 1,
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          columnGap: 4,
        }}
      >
        {CATEGORY_ORDER.map((category) => (
          <CustomizeBarSection
            key={category}
            category={category}
            inCategory={offerableInCategory(category, role, scope)}
            layout={layout}
            controls={controls}
          />
        ))}
      </Box>
    </Box>
  )
}

export default DashboardCustomizeBar
